import { useBulkUpdateThreads } from '@zuruflex/api-client/react';
import type { ThreadPatch } from '@zuruflex/api-client';
import {
  THREAD_PRIORITIES,
  type StaffMember,
  type ThreadPriority,
  type ThreadStatus,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  describeError,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@zuruflex/ui';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

/** The same three an agent sets one at a time. SNOOZED needs a date and is not here. */
const BULK_STATUSES: ThreadStatus[] = ['OPEN', 'WAITING', 'RESOLVED'];

/** What came back from one bulk patch, in the shape the alert reads. */
export interface BulkResult {
  ok: number;
  failed: { id: string; error?: string }[];
}

export interface BulkBarProps {
  ids: string[];
  staff: StaffMember[];
  /**
   * The answer, handed up rather than held here.
   *
   * When every thread takes, the selection empties and this bar unmounts —
   * and a confirmation that disappears with the control that caused it is
   * a change an agent cannot tell happened. The page owns the alert, so it
   * outlives the bar and is cleared by the next filter change instead.
   */
  onResult: (result: BulkResult) => void;
  /** Called with the ids that took, so the failures stay ticked for a retry. */
  onApplied: (succeeded: string[]) => void;
  onClear: () => void;
}

/**
 * One change across a ticked selection.
 *
 * `PATCH /v1/threads/bulk` answers per thread rather than all-or-nothing —
 * a thread a colleague resolved thirty seconds ago refuses the transition
 * and the other ninety-nine should still move — so the result is a list,
 * and it is rendered as an `Alert` rather than as a toast. There is no
 * toast primitive here, and a result an agent has to act on should not
 * evaporate after four seconds: the rows that refused are exactly the ones
 * still ticked.
 */
export function BulkBar({ ids, staff, onResult, onApplied, onClear }: BulkBarProps) {
  const { t } = useTranslation(['admin', 'common']);
  const { user } = useAuth();
  const bulk = useBulkUpdateThreads(api);

  const apply = (patch: ThreadPatch) => {
    bulk
      .mutateAsync({ ids, patch })
      .then((answer) => {
        const succeeded = answer.results.filter((row) => row.ok);
        onResult({ ok: succeeded.length, failed: answer.results.filter((row) => !row.ok) });
        onApplied(succeeded.map((row) => row.id));
      })
      .catch(() => undefined);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-accent/40 bg-accent-soft px-3 py-2">
        <span className="text-xs font-semibold text-accent-soft-text">
          {t('inbox.bulkSelected', { count: ids.length })}
        </span>

        <BulkMenu label={t('common:field.status')} disabled={bulk.isPending}>
          {BULK_STATUSES.map((status) => (
            <DropdownMenuItem key={status} onSelect={() => apply({ status })}>
              {t(`inbox.status.${status}`)}
            </DropdownMenuItem>
          ))}
        </BulkMenu>

        <BulkMenu label={t('inbox.filter.priority')} disabled={bulk.isPending}>
          {THREAD_PRIORITIES.map((priority: ThreadPriority) => (
            <DropdownMenuItem key={priority} onSelect={() => apply({ priority })}>
              {t(`inbox.priority.${priority}`)}
            </DropdownMenuItem>
          ))}
        </BulkMenu>

        <BulkMenu label={t('inbox.assignee')} disabled={bulk.isPending}>
          <DropdownMenuItem onSelect={() => apply({ assigneeId: user?.id ?? null })}>
            {t('inbox.claim')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => apply({ assigneeId: null })}>
            {t('inbox.unassigned')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('inbox.assignee')}</DropdownMenuLabel>
          {staff
            .filter((member) => member.isActive)
            .map((member) => (
              <DropdownMenuItem
                key={member.id}
                onSelect={() => apply({ assigneeId: member.id })}
              >
                {member.fullName}
              </DropdownMenuItem>
            ))}
        </BulkMenu>

        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
          {t('inbox.bulkClear')}
        </Button>
      </div>

      {/* The request itself fell over — not a per-thread refusal. */}
      {bulk.isError ? <Alert variant="danger">{describeError(bulk.error)}</Alert> : null}
    </div>
  );
}

function BulkMenu({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          {label}
          <ChevronDown className="size-3.5 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * What the bulk patch answered, kept on the page rather than in the bar.
 *
 * An `Alert` and not a toast: there is no toast primitive here, and a
 * result an agent has to act on should not evaporate after four seconds.
 * The threads that refused are named, and they are the ones still ticked.
 */
export function BulkResultAlert({ result }: { result: BulkResult }) {
  const { t } = useTranslation(['admin', 'common']);

  if (result.failed.length === 0) {
    return <Alert variant="success">{t('inbox.bulkDone', { count: result.ok })}</Alert>;
  }

  return (
    <Alert
      variant="warning"
      title={t('inbox.bulkPartial', { ok: result.ok, failed: result.failed.length })}
    >
      <ul className="space-y-0.5 text-xs">
        {result.failed.map((row) => (
          <li key={row.id}>
            <span className="font-mono">{row.id.slice(0, 8)}</span>
            {row.error ? ` — ${row.error}` : null}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
